import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSetRecoilState } from "recoil";
import { componentState } from "../../atoms/componentState";
import {
  ClienteCarteira,
  TermoDoCliente,
  TermosCarteiraApi,
} from "../../services/termosApi";

const STATUS_ROTULO: Record<string, string> = {
  PENDENTE: "Aguardando aceite",
  ACEITO: "Aceito",
  RECUSADO: "Recusado",
  EXPIRADO: "Prazo vencido",
  SUPERADO: "Substituído por versão mais nova",
  CANCELADO: "Cancelado",
};

const STATUS_COR: Record<string, string> = {
  PENDENTE: "blue",
  ACEITO: "green",
  RECUSADO: "red",
  EXPIRADO: "orange",
  SUPERADO: "gray",
  CANCELADO: "gray",
};

/**
 * Consulta dos termos dos clientes da carteira.
 *
 * Somente leitura: o supervisor lê o documento para conversar com o cliente, mas quem
 * aceita ou recusa é o master do estabelecimento.
 *
 * Nem todo cliente tem termo, e a tela precisa mostrar essa diferença — um cliente sem
 * nenhum termo não é erro de carregamento. Por isso a lista traz a contagem por cliente e
 * o filtro "só com termos" nasce ligado, para a busca não parecer quebrada.
 */
export default function TermosCarteira() {
  const setHeaderLocation = useSetRecoilState(componentState);

  const [clientes, setClientes] = useState<ClienteCarteira[]>([]);
  const [carregandoClientes, setCarregandoClientes] = useState(false);

  const [idCliente, setIdCliente] = useState<number>(0);
  const [busca, setBusca] = useState("");
  const [soComTermos, setSoComTermos] = useState(true);

  const [termos, setTermos] = useState<TermoDoCliente[]>([]);
  const [carregandoTermos, setCarregandoTermos] = useState(false);

  const [documento, setDocumento] = useState<{ titulo: string; html: string } | null>(null);
  const [abrindo, setAbrindo] = useState(false);

  useEffect(() => {
    setHeaderLocation("Termos dos Clientes");
  }, [setHeaderLocation]);

  const carregarClientes = useCallback(async () => {
    setCarregandoClientes(true);
    try {
      setClientes(await TermosCarteiraApi.getClientes());
    } catch (error) {
      setClientes([]);
    } finally {
      setCarregandoClientes(false);
    }
  }, []);

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  const opcoes = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, "");

    return clientes
      .filter((c) => (soComTermos ? c.termos > 0 : true))
      .filter((c) => {
        if (!termo) return true;
        if (c.nome.toLowerCase().includes(termo)) return true;
        return digitos.length > 0 && (c.documento ?? "").includes(digitos);
      });
  }, [clientes, busca, soComTermos]);

  const clienteSelecionado = clientes.find((c) => c.id === idCliente);

  const carregarTermos = useCallback(async (id: number) => {
    if (!id) {
      setTermos([]);
      return;
    }

    setCarregandoTermos(true);
    try {
      setTermos(await TermosCarteiraApi.getTermosDoCliente(id));
    } catch (error) {
      setTermos([]);
    } finally {
      setCarregandoTermos(false);
    }
  }, []);

  useEffect(() => {
    carregarTermos(idCliente);
  }, [idCliente, carregarTermos]);

  async function abrirDocumento(t: TermoDoCliente) {
    setAbrindo(true);
    try {
      const dados = await TermosCarteiraApi.getDocumento(idCliente, t.idTermVersion);
      setDocumento({ titulo: `${t.titulo} — v${t.versao}`, html: dados.html });
    } catch (error) {
      // documento indisponível: nada a exibir
    } finally {
      setAbrindo(false);
    }
  }

  const comTermos = clientes.filter((c) => c.termos > 0).length;

  return (
    <Flex direction="column" w="100%" h="100%" p="4" overflowY="auto">
      <Text fontSize="xl" fontWeight="bold" mb="1">
        Termos dos clientes
      </Text>
      <Text fontSize="sm" color="gray.600" mb="4">
        Leitura dos termos apresentados aos clientes da sua carteira. O aceite é feito pelo
        próprio estabelecimento — aqui você consulta o documento e a situação.
      </Text>

      <Box bgColor="#FFF" borderRadius="6px" p="4" mb="4">
        <Flex gap="3" flexWrap="wrap" align="flex-end">
          <Box>
            <Text fontSize="xs" color="gray.600" mb="1">
              Buscar
            </Text>
            <Input
              size="sm"
              w="240px"
              placeholder="Nome ou documento"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </Box>

          <Box flex="1" minW="280px">
            <Text fontSize="xs" color="gray.600" mb="1">
              Cliente
            </Text>
            <Select
              size="sm"
              placeholder={
                carregandoClientes ? "Carregando..." : `Selecione (${opcoes.length})`
              }
              value={idCliente || ""}
              onChange={(e) => setIdCliente(Number(e.target.value) || 0)}
            >
              {opcoes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {c.documento ? ` — ${c.documento}` : ""}
                  {c.termos > 0 ? ` (${c.termos} termo${c.termos > 1 ? "s" : ""})` : ""}
                  {c.bloqueado ? " · BLOQUEADO" : ""}
                </option>
              ))}
            </Select>
          </Box>

          <Button
            size="sm"
            variant={soComTermos ? "solid" : "outline"}
            colorScheme="blue"
            onClick={() => setSoComTermos(!soComTermos)}
          >
            {soComTermos ? "Só com termos" : "Todos da carteira"}
          </Button>
        </Flex>

        <Text fontSize="xs" color="gray.500" mt="2">
          {clientes.length} cliente(s) na carteira · {comTermos} com algum termo
          {comTermos < clientes.length
            ? ` · ${clientes.length - comTermos} nunca receberam termo`
            : ""}
        </Text>
      </Box>

      {!idCliente ? (
        <Alert status="info" borderRadius="6px">
          <AlertIcon />
          Escolha um cliente para ver os termos dele.
        </Alert>
      ) : carregandoTermos ? (
        <Flex justifyContent="center" py="10">
          <Spinner />
        </Flex>
      ) : termos.length === 0 ? (
        <Alert status="info" borderRadius="6px">
          <AlertIcon />
          {clienteSelecionado?.nome} ainda não recebeu nenhum termo.
        </Alert>
      ) : (
        <Box bgColor="#FFF" borderRadius="6px" p="2" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Termo</Th>
                <Th>Versão</Th>
                <Th>Situação</Th>
                <Th>Data</Th>
                <Th>Quem decidiu</Th>
                <Th>Documento</Th>
              </Tr>
            </Thead>
            <Tbody>
              {termos.map((t) => (
                <Tr key={t.idTermNotice}>
                  <Td>{t.titulo}</Td>
                  <Td>{t.versao}</Td>
                  <Td>
                    <Badge colorScheme={STATUS_COR[t.status] ?? "gray"}>
                      {STATUS_ROTULO[t.status] ?? t.status}
                    </Badge>
                    {t.motivoRecusa && (
                      <Text fontSize="xs" color="gray.600">
                        {t.motivoRecusa}
                      </Text>
                    )}
                    {t.status === "PENDENTE" && (
                      <Text fontSize="xs" color={t.vencido ? "red.500" : "gray.500"}>
                        {t.vencido
                          ? `venceu em ${moment(t.prazoAte).format("DD/MM/YY")}`
                          : `até ${moment(t.prazoAte).format("DD/MM/YY")}`}
                      </Text>
                    )}
                  </Td>
                  <Td fontSize="xs">
                    {t.decididoEm
                      ? moment(t.decididoEm).format("DD/MM/YY HH:mm")
                      : moment(t.publicadoEm).format("DD/MM/YY HH:mm")}
                  </Td>
                  <Td fontSize="xs">{t.decididoPor || "-"}</Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      isLoading={abrindo}
                      onClick={() => abrirDocumento(t)}
                    >
                      Ver
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal
        isOpen={!!documento}
        onClose={() => setDocumento(null)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">{documento?.titulo}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="5">
            <Box border="1px solid #DDD" borderRadius="6px" overflow="hidden" bgColor="#FFF">
              <iframe
                title="Termo"
                srcDoc={documento?.html ?? ""}
                sandbox="allow-same-origin"
                style={{ width: "100%", height: "65vh", border: "none", display: "block" }}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
